import { useTranslation } from 'react-i18next';
import { FilterChip } from '@/components/ui/Chip';
import {
  highlightTagColorLabelKey,
  highlightTagDisplayName,
  highlightTagInkCss,
  type HighlightTagColorSource,
} from '@/lib/highlightTagColors';
import { fileCategorySwatchClass } from '@/lib/patientFileCategories';
import { cn } from '@/utils/cn';

/**
 * Markerings-tag chips for the journal Markeringer list.
 * Same colours and names as the tags on a marking. None selected shows every
 * marking. One or more selected keeps markings that have any of those tags.
 */
export function JournalMarkingTagFilter({
  tags,
  counts,
  total,
  selectedIds,
  onToggle,
  onClear,
}: {
  tags: readonly HighlightTagColorSource[];
  counts: ReadonlyMap<string, number>;
  total: number;
  selectedIds: readonly string[];
  onToggle: (tagId: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  if (tags.length === 0) return null;
  const selected = new Set(selectedIds);
  const unnamed = t('settings.highlight_tags.unnamed');
  const allOn = selectedIds.length === 0;

  return (
    <div
      className="jw-file-chips"
      role="group"
      aria-label={t('journal.history_markings_filter_tags')}
      data-testid="journal-history-markings-tag-filter"
    >
      <FilterChip pressed={allOn} count={total} onClick={onClear}>
        {t('journal.history_markings_filter_all')}
      </FilterChip>
      {tags.map((tag) => {
        const on = selected.has(tag.id);
        const colorLabel = t(highlightTagColorLabelKey(tag.color_token));
        const label = highlightTagDisplayName(tag, unnamed, colorLabel);
        const ink = highlightTagInkCss(tag.color_token);
        return (
          <FilterChip
            key={tag.id}
            pressed={on}
            count={counts.get(tag.id) ?? 0}
            title={label}
            onClick={() => onToggle(tag.id)}
          >
            <span
              className={cn('jw-mark-tag-dot', !ink && fileCategorySwatchClass(tag.color_token))}
              style={ink ? { background: ink } : undefined}
              aria-hidden
            />
            <span className="jw-mark-tag-name">{label}</span>
          </FilterChip>
        );
      })}
    </div>
  );
}
